import React, { useState, useEffect } from "react";
import api from "../../api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./MyProfile.css";

const MyProfile = () => {
  const [adminInfo, setAdminInfo] = useState({
    username: "",
    email: "",
  });

  const [passwordData, setPasswordData] = useState({
    otp: "",
    new_password: "",
    confirm_password: "",
  });

  const [step, setStep] = useState(1); // 1: request OTP, 2: verify OTP & reset password
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const fetchAdminProfile = async () => {
      const token = localStorage.getItem("accessToken");
      const username = localStorage.getItem("username") || "Admin";

      if (!token) {
        setError("You must be logged in to view your profile.");
        setProfileLoading(false);
        return;
      }

      try {
        const response = await api.get("/api/profile/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        setAdminInfo({
          username: data.username || username,
          email: data.email || "",
        });
      } catch (err) {
        console.error("Error fetching admin profile:", err);
        // Fallback to localStorage username if API fails
        setAdminInfo({
          username: username,
          email: "",
        });
        setError("Failed to load profile. Please try again.");
      } finally {
        setProfileLoading(false);
      }
    };

    fetchAdminProfile();
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!adminInfo.email) {
      setError("Email not found. Please contact support.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/forgot-password/", {
        email: adminInfo.email,
      });

      setMessage(response.data.message || "OTP sent to your email!");
      setStep(2);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to send OTP. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!passwordData.otp) {
      setError("Please enter the OTP.");
      return;
    }

    if (!passwordData.new_password) {
      setError("Please enter a new password.");
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError("New passwords do not match.");
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/reset-password/", {
        email: adminInfo.email,
        otp: passwordData.otp,
        new_password: passwordData.new_password,
      });

      setMessage(response.data.message || "Password reset successful!");
      setPasswordData({
        otp: "",
        new_password: "",
        confirm_password: "",
      });

      // Return to step 1 after successful reset
      setTimeout(() => {
        setStep(1);
        setMessage("");
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const response = await api.post("/forgot-password/", {
        email: adminInfo.email,
      });

      setMessage(response.data.message || "New OTP sent to your email!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setStep(1);
    setPasswordData({
      otp: "",
      new_password: "",
      confirm_password: "",
    });
    setError("");
    setMessage("");
  };

  if (profileLoading) {
    return (
      <div className="my-profile-container">
        <div className="text-center mt-5">
          <div className="spinner-border text-dark" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-profile-container">
      <h2 className="profile-title">My Profile</h2>

      {/* Admin Info Section */}
      <div className="profile-info-card">
        <div className="profile-avatar">
          <span className="avatar-text">
            {adminInfo.username.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="profile-details">
          <h3 className="profile-username">{adminInfo.username}</h3>
          <p className="profile-role">Administrator</p>
          {adminInfo.email && (
            <p className="profile-email">{adminInfo.email}</p>
          )}
        </div>
      </div>

      {/* Password Reset Section */}
      <div className="password-reset-card">
        <h3 className="section-title">Change Password</h3>
        <p className="section-description">
          To change your password, verify your identity with an OTP sent to your registered email.
        </p>

        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        {step === 1 ? (
          <form onSubmit={handleRequestOTP} className="password-form">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={adminInfo.email}
                className="form-control"
                disabled
                readOnly
              />
              <small className="form-text text-muted">
                OTP will be sent to this email address.
              </small>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-submit"
              disabled={loading || !adminInfo.email}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="password-form">
            <div className="form-group">
              <label htmlFor="otp" className="form-label">
                Enter OTP
              </label>
              <input
                type="text"
                id="otp"
                name="otp"
                value={passwordData.otp}
                onChange={handlePasswordChange}
                className="form-control"
                placeholder="Enter the OTP sent to your email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="new_password" className="form-label">
                New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="new_password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  className="form-control"
                  placeholder="Enter new password"
                  required
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirm_password" className="form-label">
                Confirm New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm_password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  className="form-control"
                  placeholder="Confirm new password"
                  required
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <div className="button-group">
              <button
                type="submit"
                className="btn btn-primary btn-submit"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-cancel"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>

            <button
              type="button"
              className="btn btn-link resend-link"
              onClick={handleResendOTP}
              disabled={loading}
            >
              Resend OTP
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default MyProfile;
